import {
  Badge,
  Card,
  CardBody,
  Flex,
  HStack,
  Heading,
  Hide,
  Icon,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { OfficeBuildingIcon } from '@heroicons/react/outline';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';

import { OptionTokenStatus, useGetOptionAssetQuery } from '@blockpulse3/graphql/hooks';
import { formatDate } from '@blockpulse3/helpers';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';
import { useBadge } from '@blockpulse3/ui/ui-hooks';

type Props = unknown;

/**
 * OptionHeader.
 *
 * @returns {JSX.Element}
 */
export function OptionHeader(): JSX.Element {
  const { assetId = '', companyId = '' } = useParams();

  const { getBadge } = useBadge(
    null,
    [{ value: OptionTokenStatus.LOCKED, label: 'Verrouillé', color: 'red' }],
    { label: 'En cours', color: 'green' },
  );

  const { data, loading, error } = useGetOptionAssetQuery({
    variables: {
      getAssetInput: {
        assetId,
        companyId,
      },
    },
  });

  if (loading) {
    return (
      <Skeleton>
        <Card h="200px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="200px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="200px" />;
  }

  const option = data.getOptionAsset;
  const optionStatus = getBadge(option.data.status);

  return (
    <Card>
      <Flex as={CardBody} direction={{ base: 'column', md: 'row' }}>
        <Hide below="md">
          <Flex
            alignItems="center"
            bg="gray.50"
            boxSize="40"
            flexShrink="0"
            justifyContent="center"
            rounded="sm"
          >
            <Icon as={OfficeBuildingIcon} boxSize="52px" />
          </Flex>
        </Hide>
        <Stack
          alignItems="flex-start"
          width="full"
          px={{ base: '0', md: '6' }}
          pb={{ base: '4', md: '0' }}
          justifyContent="center"
        >
          <Text color="gray.500" fontSize="xs" fontWeight="semibold">
            {option.assetType}
          </Text>
          <Heading fontWeight="bold">{option.name}</Heading>
          <Badge colorScheme={optionStatus.color}>{optionStatus.label}</Badge>
        </Stack>
        <Flex
          direction="column"
          p="5"
          rounded="sm"
          justifyContent="center"
          bg="gray.50"
          alignItems="flex-end"
          flexShrink="0"
        >
          <HStack>
            <Text fontWeight="500" color="gray.600">
              fin le
            </Text>
          </HStack>
          <Text fontSize="2xl" color="gray.600" fontWeight="600">
            {formatDate(dayjs.unix(option.expirationDate).toDate(), 'D MMMM YYYY')}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
}

export type OptionHeaderProps = Props;
